/**
 * Studio Store
 *
 * State management for Quiz Studio wizard (Phase 3).
 * Tracks wizard progress and configuration choices across 6 steps.
 */

import { useSyncExternalStoreWithSelector } from "use-sync-external-store/with-selector";
import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import type { Category } from "@/db";

// ============================================================================
// Types
// ============================================================================

export type WizardStep = 1 | 2 | 3 | 4 | 5 | 6;

export type DatasetSource = {
  type: "catalogue" | "file" | "url";
  id?: string; // For catalogue datasets
  path?: string; // Full path for catalogue datasets
  url?: string; // For URL datasets
  filename?: string; // For file uploads
  topics?: string[]; // Topics from catalogue dataset (for category detection)
};

export type FilterRule = {
  id: string;
  propertyKey: string;
  mode: "exact" | "pattern" | "comparison" | "ranking";
  values: string[];
  // Mode-specific fields
  operator?: ">" | "<" | ">=" | "<=" | "=" | "!="; // For comparison
  rankingDirection?: "top" | "bottom"; // For ranking
  rankingCount?: number; // For ranking
};

export type WizardState = {
  // Navigation
  currentStep: WizardStep;
  completedSteps: Set<WizardStep>;

  // Step 1: Dataset Selection
  dataset: {
    source: DatasetSource | null;
    collection: FeatureCollection<Geometry, GeoJsonProperties> | null;
    label: string;
  };

  // Step 2: Prompt Configuration
  promptKeys: string[];

  // Step 3: Filter Configuration
  filters: FilterRule[];

  // Step 4: Scope & Settings
  scopeKey: string | null;
  scopeBias: number;
  repetitions: number;
  maxAttempts: number;

  // Step 5: Feature Exclusions
  excludedFeatureIds: string[];

  // Step 6: Save Configuration
  title: string;
  description: string;
  category: Category;

  // Meta
  isEditing: boolean; // True if editing existing quiz
  originalSignature: string | null; // Signature of quiz being edited
};

type Listener = () => void;
type Selector<T> = (state: WizardState) => T;

// ============================================================================
// Store Implementation
// ============================================================================

const listeners = new Set<Listener>();

const initialState: WizardState = {
  currentStep: 1,
  completedSteps: new Set(),
  dataset: {
    source: null,
    collection: null,
    label: "",
  },
  promptKeys: [],
  filters: [],
  scopeKey: null,
  scopeBias: 0.5,
  repetitions: 1,
  maxAttempts: 1,
  excludedFeatureIds: [],
  title: "",
  description: "",
  category: "admin",
  isEditing: false,
  originalSignature: null,
};

let studioState: WizardState = initialState;

function emit() {
  listeners.forEach((listener) => listener());
}

function setState(updater: (state: WizardState) => WizardState) {
  studioState = updater(studioState);
  emit();
}

function resetState(partial?: Partial<WizardState>) {
  studioState = {
    ...initialState,
    ...partial,
  };
  emit();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return studioState;
}

// ============================================================================
// Hook
// ============================================================================

export function useStudioStore<T>(
  selector: Selector<T>,
  isEqual?: (a: T, b: T) => boolean
): T {
  return useSyncExternalStoreWithSelector(
    subscribe,
    getSnapshot,
    getSnapshot,
    selector,
    isEqual
  );
}

// ============================================================================
// Actions
// ============================================================================

/**
 * Navigate to a specific step
 */
export function goToStep(step: WizardStep) {
  setState((state) => ({
    ...state,
    currentStep: step,
  }));
}

/**
 * Go to next step
 */
export function nextStep() {
  setState((state) => {
    const nextStep = Math.min(6, state.currentStep + 1) as WizardStep;
    return {
      ...state,
      currentStep: nextStep,
      completedSteps: new Set([...state.completedSteps, state.currentStep]),
    };
  });
}

/**
 * Go to previous step
 */
export function previousStep() {
  setState((state) => {
    const prevStep = Math.max(1, state.currentStep - 1) as WizardStep;
    return {
      ...state,
      currentStep: prevStep,
    };
  });
}

/**
 * Set dataset (Step 1)
 *
 * When changing datasets, reset dependent configurations:
 * - promptKeys: Old properties won't exist in new dataset
 * - filters: Old filter properties won't exist
 * - excludedFeatureIds: Old feature IDs won't exist
 */
export function setDataset(
  source: DatasetSource,
  collection: FeatureCollection<Geometry, GeoJsonProperties>,
  label: string
) {
  setState((state) => ({
    ...state,
    dataset: { source, collection, label },
    // Reset dependent configurations
    promptKeys: [],
    filters: [],
    excludedFeatureIds: [],
  }));
}

/**
 * Set prompt keys (Step 2)
 */
export function setPromptKeys(keys: string[]) {
  setState((state) => ({
    ...state,
    promptKeys: keys,
  }));
}

/**
 * Add filter rule (Step 3)
 */
export function addFilter(rule: FilterRule) {
  setState((state) => ({
    ...state,
    filters: [...state.filters, rule],
  }));
}

/**
 * Update filter rule (Step 3)
 */
export function updateFilter(id: string, updates: Partial<FilterRule>) {
  setState((state) => ({
    ...state,
    filters: state.filters.map((f) =>
      f.id === id ? { ...f, ...updates } : f
    ),
  }));
}

/**
 * Remove filter rule (Step 3)
 */
export function removeFilter(id: string) {
  setState((state) => ({
    ...state,
    filters: state.filters.filter((f) => f.id !== id),
  }));
}

/**
 * Set scope configuration (Step 4)
 */
export function setScopeConfig(config: {
  scopeKey: string | null;
  scopeBias: number;
  repetitions: number;
  maxAttempts: number;
}) {
  setState((state) => ({
    ...state,
    ...config,
  }));
}

/**
 * Toggle feature exclusion (Step 5)
 */
export function toggleFeatureExclusion(featureId: string) {
  setState((state) => {
    const excluded = state.excludedFeatureIds.includes(featureId);
    return {
      ...state,
      excludedFeatureIds: excluded
        ? state.excludedFeatureIds.filter((id) => id !== featureId)
        : [...state.excludedFeatureIds, featureId],
    };
  });
}

/**
 * Set save metadata (Step 6)
 */
export function setSaveMetadata(metadata: {
  title: string;
  description: string;
  category: Category;
}) {
  setState((state) => ({
    ...state,
    ...metadata,
  }));
}

/**
 * Reset wizard to initial state
 */
export function resetWizard() {
  resetState();
}

/**
 * Load existing configuration for editing/cloning
 *
 * This is the primary function used when cloning a quiz.
 * Pass isClone=false to edit in-place (keeps original signature).
 */
export function loadConfigForEditing(
  config: Partial<WizardState>,
  signature: string,
  isClone: boolean = true
) {
  console.log("🔧 loadConfigForEditing called:", {
    signature,
    isClone,
    hasDataset: !!config.dataset?.collection,
    datasetLabel: config.dataset?.label,
    featureCount: config.dataset?.collection?.features.length,
    promptKeys: config.promptKeys,
    filters: config.filters?.length,
    currentStep: config.currentStep,
    completedSteps: config.completedSteps,
  });

  resetState({
    ...initialState, // Start with fresh state
    ...config, // Apply loaded config
    isEditing: !isClone, // Only set editing flag if not cloning
    originalSignature: isClone ? null : signature, // Clear signature for clones
  });

  console.log("✅ loadConfigForEditing complete, state updated");
}

// ============================================================================
// Selectors
// ============================================================================

export const selectCurrentStep = (state: WizardState) => state.currentStep;
export const selectCompletedSteps = (state: WizardState) => state.completedSteps;
export const selectDataset = (state: WizardState) => state.dataset;
export const selectPromptKeys = (state: WizardState) => state.promptKeys;
export const selectFilters = (state: WizardState) => state.filters;
export const selectScopeConfig = (state: WizardState) => ({
  scopeKey: state.scopeKey,
  scopeBias: state.scopeBias,
  repetitions: state.repetitions,
  maxAttempts: state.maxAttempts,
});
export const selectExcludedFeatureIds = (state: WizardState) => state.excludedFeatureIds;
export const selectSaveMetadata = (state: WizardState) => ({
  title: state.title,
  description: state.description,
  category: state.category,
});
export const selectIsEditing = (state: WizardState) => state.isEditing;

/**
 * Check if a step is valid (can proceed)
 */
export function selectIsStepValid(step: WizardStep) {
  return (state: WizardState): boolean => {
    switch (step) {
      case 1:
        return state.dataset.collection !== null;
      case 2:
        return state.promptKeys.length > 0;
      case 3:
        return true; // Filters are optional
      case 4:
        return true; // Scope is optional
      case 5:
        return true; // Exclusions are optional
      case 6:
        return state.title.trim().length > 0;
      default:
        return false;
    }
  };
}
